import React, {
  DetailedHTMLProps,
  forwardRef,
  RefObject,
  useMemo,
} from 'react';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import {
  pluralize,
  isEmptyString,
} from '@populateui/utils';
import Button from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import head from 'lodash/head';
import AddIcon from '@mui/icons-material/Add';
import clsx from 'clsx';
import omit from 'lodash/omit';

type NewOptionProps = {
  inputValue: string;
  onAddNew: () => void;
  type: string;
  label: string;
};

function NewOption({
  inputValue,
  onAddNew,
  type,
  label = 'New',
}: NewOptionProps) {
  const preText = [label, type].join(' ')?.trim();

  return (
    <div
      tabIndex={-1}
      role="option"
      onClick={() => { onAddNew(); }}
      aria-selected="false"
      data-option-index={0}
      onKeyDown={noop}
      className="MuiAutocomplete-option flex items-center w-full gap-1 text-xs p-2 hover:bg-gray-100 rounded"
    >
      <AddIcon className="!w-4 !h-4 !text-inherit" />
      {preText}
      <strong>{inputValue}</strong>
    </div>
  );
}

const ListboxDropdown = forwardRef(({
  children,
  className,
  objectName = 'Result',
  count,
  pageProperties,
  isLoadingMore,
  fetchNextPage = noop,
  showTotalSize = false,
  searchValue,
  isSearching = false,
  onAddNew,
  label,
  type = '',
  shouldRemoveFirstOption = false,
  header,
  ...props
}: DetailedHTMLProps<any, any>, ref) => {
  const noOptionsText = useMemo(() => {
    const originalCount = count - Number(shouldRemoveFirstOption);
    const messages = [];
    if (isSearching) {
      messages.push('Searching..');
    } else if (isEmptyString(searchValue) && originalCount === 0) {
      messages.push('Start typing to see the list');
    } else if (originalCount === 0) {
      messages.push('No options found');
    }

    const message = head(messages);
    const helperText = isNil(message) ? undefined : (
      <div className="text-xs text-gray-400">
        {message}
      </div>
    );
    return originalCount === 0 ? helperText : undefined;
  }, [isSearching, searchValue, count, shouldRemoveFirstOption]);

  const placeholder = useMemo(() => (
    !isNil(onAddNew)
      ? (
        <NewOption
          inputValue={searchValue}
          onAddNew={onAddNew}
          label={label}
          type={type}
        />
      )
      : noOptionsText
  ), [noOptionsText, onAddNew, searchValue]);

  return (
    <div className="h-full relative -m-2">
      {header}
      <div
        {...omit(props, ['isLoading'])}
        className={clsx(className, '!px-2 !pb-2')}
        ref={ref as RefObject<HTMLDivElement>}
        role="listbox"
      >
        {placeholder}
        {(children ?? []).slice(Number(shouldRemoveFirstOption))}
        {pageProperties?.hasNextPage && (
        <Button
          className="!hover:bg-transparent !font-medium !text-gray-400 !w-full !text-center !p-2 !max-w-none !flex items-center !flex-row"
          variant="text"
          onClick={fetchNextPage}
          disabled={isLoadingMore}
        >
          {isLoadingMore && <Spinner className="!mr-2" />}
          <div>{isLoadingMore ? 'Loading...' : 'Load More'}</div>
        </Button>
        )}
      </div>
      {!isNil(pageProperties) && showTotalSize && (
      <div className="p-2 text-xs sticky bottom-0">
        {pluralize(objectName, pageProperties?.totalSize)}
        {' '}
        Found
      </div>
      )}
    </div>
  );
});

export default ListboxDropdown;
